import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  accessLocalStorage,
  getFail2banStatus,
  getBannedIPs,
  banIP,
  unbanIP,
  getWhitelist,
  addToWhitelist,
  removeFromWhitelist,
  getMyIP,
  getTenantWhitelist,
  addTenantAddress,
  removeTenantAddress,
} from '../../../services';

import Loading from '../../Loading/LoadingModule';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faLockOpen, faSync, faGlobe } from '@fortawesome/free-solid-svg-icons';

import AutoBlockTab from './AutoBlockTab';
import './Fail2banDashboard.css';

// IP/hostname validation
const IPV4_REGEX = /^(?:(?:25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(?:25[0-5]|2[0-4]\d|[01]?\d\d?)(\/(?:3[0-2]|[12]?\d))?$/;
const HOSTNAME_REGEX = /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;

function isValidAddress(input) {
  if (!input) return false;
  const trimmed = input.trim();
  return IPV4_REGEX.test(trimmed) || HOSTNAME_REGEX.test(trimmed);
}

function Fail2banDashboard() {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('whitelist');
  const [whitelist, setWhitelist] = useState([]);
  const [bannedIPs, setBannedIPs] = useState([]);
  const [jails, setJails] = useState([]);
  const [jailStats, setJailStats] = useState([]);
  const [totalBannedHistoric, setTotalBannedHistoric] = useState(0);
  const [filterText, setFilterText] = useState('');
  const [selectedJail, setSelectedJail] = useState('all');
  const [actionLoading, setActionLoading] = useState(false);

  // User context
  const [userRole, setUserRole] = useState('');
  const [userTenant, setUserTenant] = useState('');

  // Tenant self-service
  const [tenantWhitelist, setTenantWhitelist] = useState([]);
  const [tenantWhitelistLimit, setTenantWhitelistLimit] = useState(10);
  const [detectedIP, setDetectedIP] = useState('');

  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showConfirmRemove, setShowConfirmRemove] = useState(false);
  const [ipToRemove, setIpToRemove] = useState(null);
  const [showBanModal, setShowBanModal] = useState(false);
  const [showConfirmUnban, setShowConfirmUnban] = useState(false);
  const [ipToUnban, setIpToUnban] = useState(null);
  const [showTenantAddModal, setShowTenantAddModal] = useState(false);
  const [showConfirmTenantRemove, setShowConfirmTenantRemove] = useState(false);
  const [tenantEntryToRemove, setTenantEntryToRemove] = useState(null);

  // Inputs
  const [newIP, setNewIP] = useState('');
  const [banFormIP, setBanFormIP] = useState('');
  const [selectedJailForBan, setSelectedJailForBan] = useState('');
  const [tenantNewAddress, setTenantNewAddress] = useState('');
  const [tenantNewReason, setTenantNewReason] = useState('');

  // Alert
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('success');

  const history = useHistory();

  const showAlert = (message, type = 'success') => {
    setAlertMessage(message);
    setAlertType(type);
    setTimeout(() => setAlertMessage(''), 5000);
  };

  const isRoot = userRole === 'root';

  const fetchData = useCallback(async (role, tenant) => {
    try {
      if (role === 'root') {
        const [statusData, bannedData, whitelistData] = await Promise.all([
          getFail2banStatus(),
          getBannedIPs(),
          getWhitelist(),
        ]);
        setJails(statusData.jails || []);
        setBannedIPs(bannedData.banned || []);
        setJailStats(bannedData.jailStats || []);
        setTotalBannedHistoric(bannedData.totalBannedHistoric || 0);
        setWhitelist(whitelistData.whitelist || []);
      }

      // Tenant whitelist (for all users)
      if (tenant) {
        const [tenantData, myIPData] = await Promise.all([
          getTenantWhitelist(tenant),
          getMyIP(),
        ]);
        setTenantWhitelist(tenantData.whitelist || []);
        setTenantWhitelistLimit(tenantData.limit || 10);
        setDetectedIP(myIPData.ip || '');
      }
    } catch (error) {
      showAlert('Erro ao carregar dados do Fail2Ban', 'error');
    }
  }, []);

  const validateUserLogged = useCallback(async () => {
    const dataUser = accessLocalStorage.getUserLocalStorage();
    if (!dataUser) return history.push('/');
    const { user: { role, idtenant } } = dataUser;

    setUserRole(role);
    setUserTenant(idtenant);

    // Non-root users go directly to "Meus IPs" tab
    if (role !== 'root') {
      setActiveTab('myips');
    }

    await fetchData(role, idtenant);
    setLoading(false);
  }, [history, fetchData]);

  useEffect(() => {
    validateUserLogged();
  }, [validateUserLogged]);

  const handleRefresh = async () => {
    setActionLoading(true);
    await fetchData(userRole, userTenant);
    setActionLoading(false);
  };

  // === WHITELIST ACTIONS (root) ===

  const handleAddToWhitelist = async () => {
    if (!newIP.trim()) return;
    setActionLoading(true);
    try {
      await addToWhitelist(newIP.trim());
      showAlert(`IP ${newIP.trim()} adicionado a whitelist com sucesso`);
      setShowAddModal(false);
      setNewIP('');
      await fetchData(userRole, userTenant);
    } catch (error) {
      const msg = error.response?.data?.error || 'Erro ao adicionar IP a whitelist';
      showAlert(msg, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const confirmRemoveFromWhitelist = (ip) => {
    setIpToRemove(ip);
    setShowConfirmRemove(true);
  };

  const handleRemoveFromWhitelist = async () => {
    if (!ipToRemove) return;
    setActionLoading(true);
    setShowConfirmRemove(false);
    const ip = ipToRemove;
    setIpToRemove(null);
    try {
      await removeFromWhitelist(ip);
      showAlert(`IP ${ip} removido da whitelist com sucesso`);
      await fetchData(userRole, userTenant);
    } catch (error) {
      const msg = error.response?.data?.error || 'Erro ao remover IP da whitelist';
      showAlert(msg, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // === BAN/UNBAN ACTIONS (root) ===

  const handleBanIP = async () => {
    if (!banFormIP.trim() || !selectedJailForBan) return;
    setActionLoading(true);
    try {
      await banIP(selectedJailForBan, banFormIP.trim());
      showAlert(`IP ${banFormIP.trim()} banido na jail ${selectedJailForBan} com sucesso`);
      setShowBanModal(false);
      setBanFormIP('');
      setSelectedJailForBan('');
      await fetchData(userRole, userTenant);
    } catch (error) {
      const msg = error.response?.data?.error || 'Erro ao banir IP';
      showAlert(msg, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const confirmUnbanIP = (ip, jail) => {
    setIpToUnban({ ip, jail });
    setShowConfirmUnban(true);
  };

  const handleUnbanIP = async () => {
    if (!ipToUnban) return;
    setActionLoading(true);
    setShowConfirmUnban(false);
    const { ip, jail } = ipToUnban;
    setIpToUnban(null);
    try {
      await unbanIP(jail, ip);
      showAlert(`IP ${ip} desbanido da jail ${jail} com sucesso`);
      await fetchData(userRole, userTenant);
    } catch (error) {
      const msg = error.response?.data?.error || 'Erro ao desbanir IP';
      showAlert(msg, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // === TENANT SELF-SERVICE ACTIONS ===

  const handleAddTenantAddress = async () => {
    if (!tenantNewAddress.trim()) return;
    setActionLoading(true);
    try {
      await addTenantAddress(userTenant, tenantNewAddress.trim(), tenantNewReason.trim());
      showAlert(`${tenantNewAddress.trim()} adicionado com sucesso`);
      setShowTenantAddModal(false);
      setTenantNewAddress('');
      setTenantNewReason('');
      await fetchData(userRole, userTenant);
    } catch (error) {
      const msg = error.response?.data?.error || 'Erro ao adicionar endereco';
      showAlert(msg, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddMyIP = async () => {
    if (!detectedIP) return;
    setActionLoading(true);
    try {
      await addTenantAddress(userTenant, detectedIP, 'IP detectado automaticamente');
      showAlert(`IP ${detectedIP} adicionado com sucesso`);
      await fetchData(userRole, userTenant);
    } catch (error) {
      const msg = error.response?.data?.error || 'Erro ao adicionar IP';
      showAlert(msg, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const confirmTenantRemove = (entry) => {
    setTenantEntryToRemove(entry);
    setShowConfirmTenantRemove(true);
  };

  const handleRemoveTenantAddress = async () => {
    if (!tenantEntryToRemove) return;
    setActionLoading(true);
    setShowConfirmTenantRemove(false);
    const entry = tenantEntryToRemove;
    setTenantEntryToRemove(null);
    try {
      await removeTenantAddress(userTenant, entry.id_whitelist);
      showAlert(`${entry.address} removido com sucesso`);
      await fetchData(userRole, userTenant);
    } catch (error) {
      const msg = error.response?.data?.error || 'Erro ao remover endereco';
      showAlert(msg, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // === FILTERING ===

  const filteredWhitelist = whitelist.filter((item) =>
    item.ip.toLowerCase().includes(filterText.toLowerCase())
  );

  const filteredBanned = bannedIPs.filter((item) => {
    const matchesText = item.ip.toLowerCase().includes(filterText.toLowerCase());
    const matchesJail = selectedJail === 'all' || item.jail === selectedJail;
    return matchesText && matchesJail;
  });

  const filteredTenantWhitelist = tenantWhitelist.filter((item) =>
    item.address.toLowerCase().includes(filterText.toLowerCase())
  );

  const getJailBadgeClass = (jail) => {
    if (jail.includes('ssh')) return 'f2b-badge-jail-sshd';
    if (jail.includes('asterisk')) return 'f2b-badge-jail-asterisk';
    if (jail.includes('nginx')) return 'f2b-badge-jail-nginx';
    return 'f2b-badge-jail';
  };

  const getAddressTypeBadge = (type) => {
    switch (type) {
      case 'hostname': return { label: 'DDNS', className: 'f2b-badge-hostname' };
      case 'cidr': return { label: 'CIDR', className: 'f2b-badge-cidr' };
      default: return { label: 'IP', className: 'f2b-badge-ip' };
    }
  };

  if (loading) return <Loading />;

  return (
    <div className="f2b-container">
      {/* Header */}
      <div className="f2b-header">
        <div className="f2b-title-wrapper">
          <svg className="f2b-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
          <div>
            <h1 className="f2b-title">Fail2Ban</h1>
            <p className="f2b-subtitle">Gerenciar whitelist e IPs banidos</p>
          </div>
        </div>
        <button className="f2b-btn-refresh" onClick={handleRefresh} disabled={actionLoading}>
          <FontAwesomeIcon icon={faSync} spin={actionLoading} />
          Atualizar
        </button>
      </div>

      <div className="f2b-content">
      {/* Alert */}
      {alertMessage && (
        <div className={`f2b-alert ${
          alertType === 'success' ? 'f2b-alert-success'
            : alertType === 'warning' ? 'f2b-alert-warning'
            : 'f2b-alert-error'
        }`}>
          <span>{alertMessage}</span>
          <button className="f2b-alert-close" onClick={() => setAlertMessage('')}>&times;</button>
        </div>
      )}

      {/* Tabs */}
      <div className="f2b-tabs">
        <button
          className={`f2b-tab ${activeTab === 'myips' ? 'active' : ''}`}
          onClick={() => { setActiveTab('myips'); setFilterText(''); }}
        >
          Meus IPs
          <span className="f2b-tab-count">{tenantWhitelist.length}/{tenantWhitelistLimit}</span>
        </button>
        {isRoot && (
          <>
            <button
              className={`f2b-tab ${activeTab === 'whitelist' ? 'active' : ''}`}
              onClick={() => { setActiveTab('whitelist'); setFilterText(''); }}
            >
              Whitelist
              <span className="f2b-tab-count">{whitelist.length}</span>
            </button>
            <button
              className={`f2b-tab ${activeTab === 'banned' ? 'active' : ''}`}
              onClick={() => { setActiveTab('banned'); setFilterText(''); setSelectedJail('all'); }}
            >
              IPs Banidos
              <span className="f2b-tab-count">{bannedIPs.length}</span>
            </button>
            <button
              className={`f2b-tab ${activeTab === 'auto_block' ? 'active' : ''}`}
              onClick={() => setActiveTab('auto_block')}
            >
              Auto-Block
            </button>
          </>
        )}
      </div>

      {/* ========== AUTO-BLOCK TAB (root only) ========== */}
      {activeTab === 'auto_block' && isRoot && (
        <AutoBlockTab userRole={userRole} showAlert={showAlert} />
      )}

      {/* ========== MY IPS TAB (self-service) ========== */}
      {activeTab === 'myips' && (
        <>
          {/* Detected IP banner */}
          {detectedIP && (
            <div className="f2b-detected-ip-banner">
              <div className="f2b-detected-ip-info">
                <FontAwesomeIcon icon={faGlobe} />
                <span>Seu IP atual: <strong>{detectedIP}</strong></span>
              </div>
              <button
                className="f2b-btn-add-my-ip"
                onClick={handleAddMyIP}
                disabled={actionLoading || tenantWhitelist.some(e => e.address === detectedIP)}
              >
                {tenantWhitelist.some(e => e.address === detectedIP) ? 'Ja adicionado' : 'Adicionar meu IP'}
              </button>
            </div>
          )}

          <div className="f2b-actions-row">
            <button
              className="f2b-btn-add"
              onClick={() => setShowTenantAddModal(true)}
              disabled={actionLoading || tenantWhitelist.length >= tenantWhitelistLimit}
            >
              <FontAwesomeIcon icon={faPlus} />
              Adicionar IP/DDNS
            </button>
            <span className="f2b-slots-info">
              {tenantWhitelist.length}/{tenantWhitelistLimit} enderecos utilizados
            </span>
          </div>

          <div className="f2b-filter-section">
            <div className="f2b-filter-row">
              <div className="f2b-filter-input-wrapper">
                <svg className="f2b-filter-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  className="f2b-filter-input"
                  placeholder="Filtrar..."
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                />
                {filterText && (
                  <button className="f2b-filter-clear" onClick={() => setFilterText('')}>&times;</button>
                )}
              </div>
            </div>
          </div>

          {tenantWhitelist.length === 0 ? (
            <div className="f2b-empty-state">
              <svg className="f2b-empty-state-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <div className="f2b-empty-state-title">Nenhum IP cadastrado</div>
              <p className="f2b-empty-state-text">
                Adicione IPs ou hostnames DDNS para evitar banimentos indevidos.
              </p>
            </div>
          ) : (
            <div className="f2b-table-wrapper">
              <table className="f2b-table">
                <thead className="f2b-table-header">
                  <tr>
                    <th>Endereco</th>
                    <th>Tipo</th>
                    <th>Motivo</th>
                    <th>Adicionado por</th>
                    <th>Acoes</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTenantWhitelist.map((item) => {
                    const badge = getAddressTypeBadge(item.address_type);
                    return (
                      <tr key={item.id_whitelist}>
                        <td><span className="f2b-ip-text">{item.address}</span></td>
                        <td><span className={`f2b-badge ${badge.className}`}>{badge.label}</span></td>
                        <td><span className="f2b-reason-text">{item.reason || '-'}</span></td>
                        <td><span className="f2b-meta-text">{item.created_by}</span></td>
                        <td>
                          <div className="f2b-actions-cell">
                            <button
                              className="f2b-btn-action f2b-btn-delete"
                              onClick={() => confirmTenantRemove(item)}
                              title="Remover"
                              disabled={actionLoading}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ========== WHITELIST TAB (root only) ========== */}
      {activeTab === 'whitelist' && isRoot && (
        <>
          <div className="f2b-actions-row">
            <button className="f2b-btn-add" onClick={() => setShowAddModal(true)} disabled={actionLoading}>
              <FontAwesomeIcon icon={faPlus} />
              Adicionar IP
            </button>
          </div>

          <div className="f2b-filter-section">
            <div className="f2b-filter-row">
              <div className="f2b-filter-input-wrapper">
                <svg className="f2b-filter-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  className="f2b-filter-input"
                  placeholder="Filtrar por IP..."
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                />
                {filterText && (
                  <button className="f2b-filter-clear" onClick={() => setFilterText('')}>&times;</button>
                )}
              </div>
            </div>
            <div className="f2b-filter-results">
              Exibindo {filteredWhitelist.length} de {whitelist.length} IP(s)
            </div>
          </div>

          {whitelist.length === 0 ? (
            <div className="f2b-empty-state">
              <svg className="f2b-empty-state-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <div className="f2b-empty-state-title">Nenhum IP na whitelist</div>
              <p className="f2b-empty-state-text">
                Clique em "Adicionar IP" para adicionar um IP a whitelist.
              </p>
            </div>
          ) : (
            <div className="f2b-table-wrapper">
              <table className="f2b-table">
                <thead className="f2b-table-header">
                  <tr>
                    <th>IP</th>
                    <th>Status</th>
                    <th>Acoes</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredWhitelist.map((item) => (
                    <tr key={item.ip}>
                      <td>
                        <span className="f2b-ip-text">{item.ip}</span>
                      </td>
                      <td>
                        {item.protected ? (
                          <span className="f2b-badge f2b-badge-protected">
                            Protegido
                          </span>
                        ) : (
                          <span className="f2b-badge" style={{ background: '#dbeafe', color: '#1e40af' }}>
                            Personalizado
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="f2b-actions-cell">
                          {!item.protected && (
                            <button
                              className="f2b-btn-action f2b-btn-delete"
                              onClick={() => confirmRemoveFromWhitelist(item.ip)}
                              title="Remover da whitelist"
                              disabled={actionLoading}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ========== BANNED TAB (root only) ========== */}
      {activeTab === 'banned' && isRoot && (
        <>
          {jailStats.length > 0 && (
            <div className="f2b-jail-stats-grid">
              {jailStats.map((stat) => (
                <div key={stat.jail} className="f2b-jail-stat-card">
                  <div className="f2b-jail-stat-header">
                    <span className={`f2b-badge ${getJailBadgeClass(stat.jail)}`}>{stat.jail}</span>
                  </div>
                  <div className="f2b-jail-stat-body">
                    <div className="f2b-jail-stat-row">
                      <span className="f2b-jail-stat-label">Banidos agora</span>
                      <span className={`f2b-jail-stat-value ${stat.currentlyBanned > 0 ? 'f2b-stat-danger' : ''}`}>{stat.currentlyBanned}</span>
                    </div>
                    <div className="f2b-jail-stat-row">
                      <span className="f2b-jail-stat-label">Total historico</span>
                      <span className="f2b-jail-stat-value">{stat.totalBanned}</span>
                    </div>
                    <div className="f2b-jail-stat-row">
                      <span className="f2b-jail-stat-label">Falhas atuais</span>
                      <span className={`f2b-jail-stat-value ${stat.currentlyFailed > 0 ? 'f2b-stat-warning' : ''}`}>{stat.currentlyFailed}</span>
                    </div>
                    <div className="f2b-jail-stat-row">
                      <span className="f2b-jail-stat-label">Total falhas</span>
                      <span className="f2b-jail-stat-value">{stat.totalFailed}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="f2b-actions-row">
            <button className="f2b-btn-add" onClick={() => { setShowBanModal(true); setSelectedJailForBan(jails[0] || ''); }} disabled={actionLoading}>
              <FontAwesomeIcon icon={faPlus} />
              Banir IP
            </button>
            {totalBannedHistoric > 0 && (
              <span className="f2b-slots-info">
                {bannedIPs.length} ativo(s) / {totalBannedHistoric} total historico
              </span>
            )}
          </div>

          <div className="f2b-filter-section">
            <div className="f2b-filter-row">
              <div className="f2b-filter-input-wrapper">
                <svg className="f2b-filter-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  className="f2b-filter-input"
                  placeholder="Filtrar por IP..."
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                />
                {filterText && (
                  <button className="f2b-filter-clear" onClick={() => setFilterText('')}>&times;</button>
                )}
              </div>
              <select
                className="f2b-filter-select"
                value={selectedJail}
                onChange={(e) => setSelectedJail(e.target.value)}
              >
                <option value="all">Todas as Jails</option>
                {jails.map((jail) => (
                  <option key={jail} value={jail}>{jail}</option>
                ))}
              </select>
            </div>
            <div className="f2b-filter-results">
              Exibindo {filteredBanned.length} de {bannedIPs.length} IP(s) banido(s)
            </div>
          </div>

          {bannedIPs.length === 0 ? (
            <div className="f2b-empty-state">
              <svg className="f2b-empty-state-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <div className="f2b-empty-state-title">Nenhum IP banido no momento</div>
              <p className="f2b-empty-state-text">
                {totalBannedHistoric > 0
                  ? `${totalBannedHistoric} ban(s) historico(s) registrado(s). Nenhum IP esta ativamente banido agora.`
                  : 'Nenhum IP foi banido ate o momento.'
                }
              </p>
            </div>
          ) : (
            <div className="f2b-table-wrapper">
              <table className="f2b-table">
                <thead className="f2b-table-header">
                  <tr>
                    <th>IP</th>
                    <th>Jail</th>
                    <th>Acoes</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBanned.map((item, index) => (
                    <tr key={`${item.ip}-${item.jail}-${index}`}>
                      <td>
                        <span className="f2b-ip-text">{item.ip}</span>
                      </td>
                      <td>
                        <span className={`f2b-badge ${getJailBadgeClass(item.jail)}`}>
                          {item.jail}
                        </span>
                      </td>
                      <td>
                        <div className="f2b-actions-cell">
                          <button
                            className="f2b-btn-action f2b-btn-unban"
                            onClick={() => confirmUnbanIP(item.ip, item.jail)}
                            title="Desbanir IP"
                            disabled={actionLoading}
                          >
                            <FontAwesomeIcon icon={faLockOpen} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ========== MODAL: Add to Whitelist (root) ========== */}
      {showAddModal && (
        <div className="f2b-modal-overlay">
          <div className="f2b-modal-card">
            <header className="f2b-modal-header">
              <p className="f2b-modal-title">Adicionar IP a Whitelist</p>
              <button className="f2b-modal-close-btn" onClick={() => { setShowAddModal(false); setNewIP(''); }}>&times;</button>
            </header>
            <section className="f2b-modal-body">
              <div className="f2b-modal-field">
                <label className="f2b-modal-label">Endereco IP, CIDR ou Hostname</label>
                <input
                  type="text"
                  className="f2b-modal-input"
                  placeholder="Ex: 200.174.142.40, 10.0.0.0/8 ou cliente.ddns.net"
                  value={newIP}
                  onChange={(e) => setNewIP(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddToWhitelist()}
                />
              </div>
            </section>
            <footer className="f2b-modal-footer">
              <button className="f2b-btn-modal f2b-btn-modal-cancel" onClick={() => { setShowAddModal(false); setNewIP(''); }}>
                Cancelar
              </button>
              <button className="f2b-btn-modal f2b-btn-modal-confirm" onClick={handleAddToWhitelist} disabled={!newIP.trim() || actionLoading}>
                {actionLoading ? 'Adicionando...' : 'Adicionar'}
              </button>
            </footer>
          </div>
        </div>
      )}

      {/* ========== MODAL: Tenant Add IP/DDNS ========== */}
      {showTenantAddModal && (
        <div className="f2b-modal-overlay">
          <div className="f2b-modal-card">
            <header className="f2b-modal-header">
              <p className="f2b-modal-title">Adicionar IP ou Hostname DDNS</p>
              <button className="f2b-modal-close-btn" onClick={() => { setShowTenantAddModal(false); setTenantNewAddress(''); setTenantNewReason(''); }}>&times;</button>
            </header>
            <section className="f2b-modal-body">
              <div className="f2b-modal-field">
                <label className="f2b-modal-label">Endereco IP ou Hostname DDNS</label>
                <input
                  type="text"
                  className="f2b-modal-input"
                  placeholder="Ex: 200.174.142.40 ou meuip.ddns.net"
                  value={tenantNewAddress}
                  onChange={(e) => setTenantNewAddress(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddTenantAddress()}
                />
                {tenantNewAddress && !isValidAddress(tenantNewAddress) && (
                  <span className="f2b-input-error">Formato invalido. Use IP (ex: 200.174.142.40) ou hostname (ex: meuip.ddns.net)</span>
                )}
              </div>
              <div className="f2b-modal-field">
                <label className="f2b-modal-label">Motivo (opcional)</label>
                <input
                  type="text"
                  className="f2b-modal-input"
                  placeholder="Ex: Escritorio principal"
                  value={tenantNewReason}
                  onChange={(e) => setTenantNewReason(e.target.value)}
                />
              </div>
            </section>
            <footer className="f2b-modal-footer">
              <button className="f2b-btn-modal f2b-btn-modal-cancel" onClick={() => { setShowTenantAddModal(false); setTenantNewAddress(''); setTenantNewReason(''); }}>
                Cancelar
              </button>
              <button
                className="f2b-btn-modal f2b-btn-modal-confirm"
                onClick={handleAddTenantAddress}
                disabled={!tenantNewAddress.trim() || !isValidAddress(tenantNewAddress) || actionLoading}
              >
                {actionLoading ? 'Adicionando...' : 'Adicionar'}
              </button>
            </footer>
          </div>
        </div>
      )}

      {/* ========== MODAL: Confirm Remove from Whitelist (root) ========== */}
      {showConfirmRemove && (
        <div className="f2b-modal-overlay">
          <div className="f2b-modal-card">
            <header className="f2b-modal-header f2b-modal-header-danger">
              <p className="f2b-modal-title">Confirmar Remocao</p>
              <button className="f2b-modal-close-btn" onClick={() => { setShowConfirmRemove(false); setIpToRemove(null); }}>&times;</button>
            </header>
            <section className="f2b-modal-body">
              <p>
                Tem certeza que deseja remover <strong className="f2b-ip-text">{ipToRemove}</strong> da whitelist?
              </p>
              <div className="f2b-modal-warning">
                <strong>Este IP podera ser banido pelo Fail2Ban apos a remocao!</strong>
              </div>
            </section>
            <footer className="f2b-modal-footer">
              <button className="f2b-btn-modal f2b-btn-modal-cancel" onClick={() => { setShowConfirmRemove(false); setIpToRemove(null); }}>
                Cancelar
              </button>
              <button className="f2b-btn-modal f2b-btn-modal-danger" onClick={handleRemoveFromWhitelist} disabled={actionLoading}>
                {actionLoading ? 'Removendo...' : 'Sim, Remover'}
              </button>
            </footer>
          </div>
        </div>
      )}

      {/* ========== MODAL: Confirm Tenant Remove ========== */}
      {showConfirmTenantRemove && (
        <div className="f2b-modal-overlay">
          <div className="f2b-modal-card">
            <header className="f2b-modal-header f2b-modal-header-danger">
              <p className="f2b-modal-title">Confirmar Remocao</p>
              <button className="f2b-modal-close-btn" onClick={() => { setShowConfirmTenantRemove(false); setTenantEntryToRemove(null); }}>&times;</button>
            </header>
            <section className="f2b-modal-body">
              <p>
                Remover <strong className="f2b-ip-text">{tenantEntryToRemove?.address}</strong> da sua whitelist?
              </p>
              <div className="f2b-modal-warning">
                <strong>Este endereco podera ser banido pelo Fail2Ban apos a remocao!</strong>
              </div>
            </section>
            <footer className="f2b-modal-footer">
              <button className="f2b-btn-modal f2b-btn-modal-cancel" onClick={() => { setShowConfirmTenantRemove(false); setTenantEntryToRemove(null); }}>
                Cancelar
              </button>
              <button className="f2b-btn-modal f2b-btn-modal-danger" onClick={handleRemoveTenantAddress} disabled={actionLoading}>
                {actionLoading ? 'Removendo...' : 'Sim, Remover'}
              </button>
            </footer>
          </div>
        </div>
      )}

      {/* ========== MODAL: Ban IP (root) ========== */}
      {showBanModal && (
        <div className="f2b-modal-overlay">
          <div className="f2b-modal-card">
            <header className="f2b-modal-header f2b-modal-header-danger">
              <p className="f2b-modal-title">Banir IP</p>
              <button className="f2b-modal-close-btn" onClick={() => { setShowBanModal(false); setBanFormIP(''); setSelectedJailForBan(''); }}>&times;</button>
            </header>
            <section className="f2b-modal-body">
              <div className="f2b-modal-field">
                <label className="f2b-modal-label">Jail</label>
                <select
                  className="f2b-modal-select"
                  value={selectedJailForBan}
                  onChange={(e) => setSelectedJailForBan(e.target.value)}
                >
                  {jails.map((jail) => (
                    <option key={jail} value={jail}>{jail}</option>
                  ))}
                </select>
              </div>
              <div className="f2b-modal-field">
                <label className="f2b-modal-label">Endereco IP</label>
                <input
                  type="text"
                  className="f2b-modal-input"
                  placeholder="Ex: 203.0.113.5"
                  value={banFormIP}
                  onChange={(e) => setBanFormIP(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleBanIP()}
                />
              </div>
            </section>
            <footer className="f2b-modal-footer">
              <button className="f2b-btn-modal f2b-btn-modal-cancel" onClick={() => { setShowBanModal(false); setBanFormIP(''); setSelectedJailForBan(''); }}>
                Cancelar
              </button>
              <button className="f2b-btn-modal f2b-btn-modal-danger" onClick={handleBanIP} disabled={!banFormIP.trim() || !selectedJailForBan || actionLoading}>
                {actionLoading ? 'Banindo...' : 'Banir'}
              </button>
            </footer>
          </div>
        </div>
      )}

      {/* ========== MODAL: Confirm Unban (root) ========== */}
      {showConfirmUnban && (
        <div className="f2b-modal-overlay">
          <div className="f2b-modal-card">
            <header className="f2b-modal-header">
              <p className="f2b-modal-title">Confirmar Desbanimento</p>
              <button className="f2b-modal-close-btn" onClick={() => { setShowConfirmUnban(false); setIpToUnban(null); }}>&times;</button>
            </header>
            <section className="f2b-modal-body">
              <p>
                Desbanir <strong className="f2b-ip-text">{ipToUnban?.ip}</strong> da jail <strong>{ipToUnban?.jail}</strong>?
              </p>
            </section>
            <footer className="f2b-modal-footer">
              <button className="f2b-btn-modal f2b-btn-modal-cancel" onClick={() => { setShowConfirmUnban(false); setIpToUnban(null); }}>
                Cancelar
              </button>
              <button className="f2b-btn-modal f2b-btn-modal-confirm" onClick={handleUnbanIP} disabled={actionLoading}>
                {actionLoading ? 'Desbanindo...' : 'Sim, Desbanir'}
              </button>
            </footer>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}

export default Fail2banDashboard;
